/**
 * SpreadsheetConnectDialog — V1 "Foglio di Calcolo" parity, 2-step flow
 * (May 28, 2026 rewrite, closes 4 RED gaps from the V1→V2 audit).
 *
 * V1 source-of-truth: ``ando-frontend/app/components/enterprise/ConnectorWizard.tsx``
 * (the ``spreadsheetSource`` / ``uploadedFile`` / ``spreadsheetSelectedSheets``
 * / ``selectedCloudFile`` / ``selectedDriveConn`` inline state machine).
 *
 * Flow:
 *
 *   ┌────────── Step 1 (source) ──────────┐    ┌──── Step 2 (preview) ────┐
 *   │  upload:        drag&drop / browse  │    │  • Sheet checkbox list   │
 *   │  google_drive:  native Google       │ →  │  • Sample rows table     │
 *   │                 Picker (file mode)  │    │  • Connector name        │
 *   │  onedrive:      Sfoglia OneDrive    │    │  • Warnings banner       │
 *   │                 (folder picker)     │    │  • Indietro + Importa    │
 *   └─────────────────────────────────────┘    └──────────────────────────┘
 *
 * Architecture (May 28, 2026 split):
 *
 *   This file owns the Radix Dialog chrome + per-step delegation. All
 *   state orchestration (preview mutation, file/cloud selection, sheet
 *   picking, submit) lives in ``./_spreadsheet/useSpreadsheetConnectState``.
 *
 *   Extracted to ``./_spreadsheet/`` (intentionally underscored — these
 *   are private to the dialog and NOT exported through the connectors
 *   barrel):
 *     - ``SpreadsheetSourceStep`` — Step 1 source picker (upload + cloud)
 *     - ``SpreadsheetPreviewStep`` — Step 2 preview + sheet selection
 *     - ``useSpreadsheetConnectState`` — orchestration hook
 *     - ``types.tsx`` — shared types, label maps, format helpers,
 *       and the public ``Picker*`` contract (re-exported below).
 *
 * Public API: unchanged. ``$capability.tsx`` imports
 * ``{ SpreadsheetConnectDialog, type SpreadsheetSourceMode }`` from the
 * connectors barrel — both still resolve to this file.
 *
 * A11y baseline (preserved):
 *   - Radix Dialog (focus trap + Esc).
 *   - Drop zone: ``role="button" tabIndex={0}`` + Enter/Space handlers.
 *   - Per-sheet ``<label htmlFor>`` ties the checkbox + sheet name.
 *   - Sample table has ``aria-label`` describing which sheet it shows.
 *   - Inline error → ``role="alert" aria-live="polite"`` via
 *     ``<InlineErrorBanner>``.
 *   - Warnings banner uses the same alert pattern with
 *     ``tone="warning"``.
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { AlertCircle, ExternalLink } from "lucide-react";

import * as React from "react";
import { ConnectorDialogShell } from "./ConnectorDialogShell";

import { type BaseConnection, type CapabilityWizardSpec, cn, localize } from "@ando/ui";

import type { ConnectionSummary } from "@ando/core";

import type { ImportSpreadsheetOptions } from "../api/spreadsheetApi";
import type { SpreadsheetSourceMode } from "../wizards/spreadsheetSpec";
import { VendorBrandBadge } from "./VendorBrandBadge";
import { SpreadsheetPreviewStep } from "./_spreadsheet/SpreadsheetPreviewStep";
import { SpreadsheetSourceStep } from "./_spreadsheet/SpreadsheetSourceStep";
import { type PickerOpener, SOURCE_LABELS, SOURCE_PROVIDER_LABEL } from "./_spreadsheet/types";
import { useSpreadsheetConnectState } from "./_spreadsheet/useSpreadsheetConnectState";

// Re-export for backwards compatibility — these are the public API
// surface of the dialog (consumed via the connectors barrel).
export type { SpreadsheetSourceMode };
export type {
  PickerFileSelection,
  PickerOpenInput,
  PickerOpenOutcome,
  PickerOpener,
} from "./_spreadsheet/types";

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface SpreadsheetConnectDialogProps {
  /** Controlled open state. */
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Spec — drives the dialog header/benefits + brand badge. */
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  /** Pre-selected source-mode chosen via AddAccountMenu. */
  readonly sourceMode: SpreadsheetSourceMode;
  /**
   * Whether the user already has the required cloud-provider connection.
   * Drives the "Collega prima Drive/OneDrive" callout. Always ``true``
   * for ``upload`` mode.
   */
  readonly providerConnected: boolean;
  /** Fired when the user clicks the "Collega Drive/OneDrive" callout. */
  readonly onNavigateToDriveSetup: (provider: "google_drive" | "onedrive") => void;
  /**
   * Submit handler — caller wires the ``useImportSpreadsheet`` mutation.
   * Returns the freshly created connector id so the dialog can route
   * the user to the dashboard.
   */
  readonly onSubmit: (
    options: ImportSpreadsheetOptions,
  ) => Promise<{ readonly connector_id: string }>;
  /** Called after a successful import with the new connector id. */
  readonly onSuccess: (connectorId: string) => void;
  /**
   * Cloud-provider connections (filtered to the active source's vendor)
   * — drives the multi-account chip selector. Pass an empty array when
   * ``sourceMode === "upload"``. Optional so existing callers that don't
   * yet wire the multi-account selector keep working — when missing the
   * dialog simply doesn't render the chip row.
   */
  readonly cloudConnections?: readonly ConnectionSummary[];
  /**
   * Picker opener (typically ``useFilePicker.open``). Required for cloud
   * source modes; when missing, the cloud panels render a clear "Picker
   * non disponibile" hint so the user understands the missing wiring is
   * not a bug. ``upload`` mode never needs this.
   */
  readonly openPicker?: PickerOpener;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SpreadsheetConnectDialog: React.FC<SpreadsheetConnectDialogProps> = ({
  open,
  onOpenChange,
  spec,
  sourceMode,
  providerConnected,
  onNavigateToDriveSetup,
  onSubmit,
  onSuccess,
  cloudConnections = [],
  openPicker,
}) => {
  const { i18n } = useLingui();
  const state = useSpreadsheetConnectState({
    open,
    sourceMode,
    cloudConnections,
    openPicker,
    onSubmit,
    onSuccess,
  });

  const isCloudMode = sourceMode !== "upload";
  const cloudProvider = sourceMode as Exclude<SpreadsheetSourceMode, "upload">;
  const showProviderCallout = isCloudMode && !providerConnected;
  const titleId = React.useId();

  return (
    <ConnectorDialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={i18n._(msg`Importa foglio: ${SOURCE_LABELS[sourceMode]}`)}
      size="lg"
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="min-w-0 pr-8">
        <h2
          id={titleId}
          className="font-display font-semibold text-[15px] text-ink-1 leading-tight"
        >
          {state.onStep2
            ? i18n._(msg`Importa foglio · Anteprima`)
            : i18n._(msg`Importa foglio di calcolo · ${SOURCE_LABELS[sourceMode]}`)}
        </h2>
        <p className="mt-1 text-[12px] text-ink-2 leading-snug">{localize(spec.subtitle, i18n)}</p>
      </div>

      {/* ── Provider gating callout (cloud modes only) ─────────── */}
      {showProviderCallout ? (
        <div className="mt-4 flex items-start gap-3">
          <VendorBrandBadge vendorId={sourceMode} fallbackIcon={spec.icon} size="lg" />
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => onNavigateToDriveSetup(cloudProvider)}
              className={cn(
                "group flex w-full items-start gap-2 rounded-md border border-warning/40 bg-warning/[0.08] px-3 py-2.5 text-left transition-colors",
                "hover:bg-warning/[0.14]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning",
              )}
              data-testid="provider-required-callout"
            >
              <AlertCircle className="h-4 w-4 shrink-0 translate-y-px text-warning" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-[13px] text-ink-1">
                  <Trans>Devi prima collegare {SOURCE_PROVIDER_LABEL[cloudProvider]}</Trans>
                </span>
                <span className="mt-0.5 block text-[11.5px] text-ink-3 leading-snug">
                  <Trans>
                    Clicca qui per aprire la pagina Documenti e collegare un account{" "}
                    {SOURCE_PROVIDER_LABEL[cloudProvider]}.
                  </Trans>
                </span>
              </span>
              <ExternalLink
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-3 transition-colors group-hover:text-ink-1"
                aria-hidden
              />
            </button>
          </div>
        </div>
      ) : state.onStep2 && state.preview !== null ? (
        <SpreadsheetPreviewStep
          spec={spec}
          sourceMode={sourceMode}
          preview={state.preview}
          selectedSheets={state.selectedSheets}
          allSelected={state.allSheetsSelected}
          connectorName={state.connectorName}
          fileName={state.file?.name ?? state.cloudFile?.name ?? ""}
          fileSize={state.file?.size ?? state.cloudFile?.size_bytes}
          isCloud={isCloudMode}
          error={state.error}
          submitting={state.submitting}
          uploadProgress={state.uploadProgress}
          onCancelUpload={state.cancelUpload}
          onConnectorNameChange={state.setConnectorName}
          onToggleSheet={state.toggleSheet}
          onToggleAll={state.toggleAllSheets}
          onBack={state.handleBack}
          onSubmit={state.handleSubmit}
        />
      ) : (
        <SpreadsheetSourceStep
          spec={spec}
          sourceMode={sourceMode}
          cloudConnections={cloudConnections}
          selectedConnectionKey={state.selectedConnectionKey}
          onPickConnection={state.setSelectedConnectionKey}
          previewLoading={state.previewLoading}
          error={state.error}
          file={state.file}
          cloudFile={state.cloudFile}
          dragOver={state.dragOver}
          fileInputRef={state.fileInputRef}
          onDragOver={state.onDragOver}
          onDragLeave={state.onDragLeave}
          onDrop={state.onDrop}
          onDropzoneClick={state.onDropzoneClick}
          onFileChange={state.onFileChange}
          onOpenCloudPicker={state.handleOpenCloudPicker}
          pickerAvailable={openPicker !== undefined}
        />
      )}
    </ConnectorDialogShell>
  );
};

SpreadsheetConnectDialog.displayName = "SpreadsheetConnectDialog";
